import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

const ResizableContainer = styled.div<{ $gap?: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  gap: ${({ $gap }) => $gap !== undefined ? `${$gap}px` : '8px'};
`;

// Panels are sized by flex-grow PROPORTION (flex-basis 0), not width/height %.
// This way the fixed-width resizer + gaps are subtracted from the available
// space first and the panels split what's left — so two panels at 67/33 never
// sum past 100% and overflow-clip the last panel (which was hiding the pair
// Start button once the resizers were enabled).
const Panel = styled.div<{ $flex?: number }>`
  flex: ${({ $flex }) => $flex ?? 1} 1 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

// Visible divider is a hair-thin 1px line; an invisible ::before extends the
// grab area to ~7px so it's still easy to drag.
const Resizer = styled.div<{ $direction: 'horizontal' | 'vertical' }>`
  ${({ $direction, theme }) =>
    $direction === 'horizontal'
      ? `
    width: 1px;
    cursor: col-resize;
    background: ${theme.colors.border.primary};
    &::before { content: ''; position: absolute; top: 0; bottom: 0; left: -3px; right: -3px; }
    &:hover {
      background: ${theme.colors.primary};
    }
  `
      : `
    height: 1px;
    cursor: row-resize;
    background: ${theme.colors.border.primary};
    &::before { content: ''; position: absolute; left: 0; right: 0; top: -3px; bottom: -3px; }
    &:hover {
      background: ${theme.colors.primary};
    }
  `}
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  transition: background 0.2s;
`;

interface ResizablePanelProps {
  direction?: 'horizontal' | 'vertical';
  children: React.ReactNode[];
  initialSizes?: number[]; // percentages or flex ratios
  minSizes?: number[]; // minimum sizes in pixels
  maxSizes?: number[]; // maximum sizes in pixels
  disabled?: boolean; // disable dragging/resizing
  gap?: number; // gap between panels in pixels
  onResize?: (sizes: number[]) => void;
  /** When set, the dragged sizes persist to localStorage under this key and are
   *  restored on next mount (per-browser). */
  storageKey?: string;
}

/** Read a persisted size array of the expected length, or null. */
function readStoredSizes(storageKey: string | undefined, count: number): number[] | null {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length === count && parsed.every((n) => typeof n === 'number' && isFinite(n))) {
      return parsed;
    }
  } catch {
    /* ignore corrupt entry */
  }
  return null;
}

const ResizablePanel: React.FC<ResizablePanelProps> = ({
  direction = 'horizontal',
  children,
  initialSizes,
  minSizes = [],
  maxSizes = [],
  disabled = false,
  gap = 8,
  onResize,
  storageKey,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sizes, setSizes] = useState<number[]>(() => {
    const stored = readStoredSizes(storageKey, children.length);
    if (stored) return stored;
    if (initialSizes) {
      return initialSizes;
    }
    // Default: equal sizes
    return new Array(children.length).fill(100 / children.length);
  });
  const latestSizesRef = useRef<number[]>(sizes);
  const [isResizing, setIsResizing] = useState(false);
  const resizerIndexRef = useRef<number>(-1);
  const startPosRef = useRef<number>(0);
  const startSizesRef = useRef<number[]>([]);

  const handleMouseDown = useCallback(
    (index: number, e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      resizerIndexRef.current = index;
      
      // Store the absolute position relative to viewport
      startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
      startSizesRef.current = [...sizes];

      const handleMouseMove = (e: MouseEvent) => {
        if (!containerRef.current || resizerIndexRef.current === -1) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerSize =
          direction === 'horizontal' ? containerRect.width : containerRect.height;
        
        // Calculate position relative to container, not viewport
        const containerStart = direction === 'horizontal' ? containerRect.left : containerRect.top;
        const currentPos = (direction === 'horizontal' ? e.clientX : e.clientY) - containerStart;
        const startPos = startPosRef.current - containerStart;
        const delta = currentPos - startPos;
        const deltaPercent = (delta / containerSize) * 100;

        const newSizes = [...startSizesRef.current];
        const leftIndex = resizerIndexRef.current;
        const rightIndex = leftIndex + 1;

        if (leftIndex >= 0 && rightIndex < newSizes.length) {
          const leftSize = newSizes[leftIndex];
          const rightSize = newSizes[rightIndex];

          // Calculate new sizes
          let newLeftSize = leftSize + deltaPercent;
          let newRightSize = rightSize - deltaPercent;

          // Apply minimum size constraints
          if (minSizes.length > leftIndex) {
            const minSizePercent = (minSizes[leftIndex] / containerSize) * 100;
            if (newLeftSize < minSizePercent) {
              newLeftSize = minSizePercent;
              newRightSize = leftSize + rightSize - newLeftSize;
            }
          }
          if (minSizes.length > rightIndex) {
            const minSizePercent = (minSizes[rightIndex] / containerSize) * 100;
            if (newRightSize < minSizePercent) {
              newRightSize = minSizePercent;
              newLeftSize = leftSize + rightSize - newRightSize;
            }
          }

          // Apply maximum size constraints - prevent dragging beyond maximum
          if (maxSizes.length > leftIndex) {
            const maxSizePercent = (maxSizes[leftIndex] / containerSize) * 100;
            if (newLeftSize > maxSizePercent) {
              // If already at or above maximum and trying to increase further, prevent dragging
              if (leftSize >= maxSizePercent && deltaPercent > 0) {
                return; // Don't update sizes if already at max and trying to increase
              }
              newLeftSize = maxSizePercent;
              newRightSize = leftSize + rightSize - newLeftSize;
            }
          }
          if (maxSizes.length > rightIndex) {
            const maxSizePercent = (maxSizes[rightIndex] / containerSize) * 100;
            if (newRightSize > maxSizePercent) {
              // If already at or above maximum and trying to increase further, prevent dragging
              if (rightSize >= maxSizePercent && deltaPercent < 0) {
                return; // Don't update sizes if already at max and trying to increase
              }
              newRightSize = maxSizePercent;
              newLeftSize = leftSize + rightSize - newRightSize;
            }
          }

          // Ensure sizes don't exceed 100%
          const totalSize = newLeftSize + newRightSize;
          if (totalSize > 100) {
            const excess = totalSize - 100;
            if (newLeftSize > newRightSize) {
              newLeftSize -= excess;
            } else {
              newRightSize -= excess;
            }
          }

          newSizes[leftIndex] = Math.max(0, Math.min(100, newLeftSize));
          newSizes[rightIndex] = Math.max(0, Math.min(100, newRightSize));

          // Normalize to ensure they sum to 100%
          const normalizedTotal = newSizes[leftIndex] + newSizes[rightIndex];
          if (normalizedTotal > 0) {
            newSizes[leftIndex] = (newSizes[leftIndex] / normalizedTotal) * 100;
            newSizes[rightIndex] = (newSizes[rightIndex] / normalizedTotal) * 100;
          }

          // Update sizes directly via DOM to avoid re-renders. Panels are
          // flex-grow proportions (basis 0), so the resizer/gap px never cause
          // overflow regardless of direction.
          const panels = containerRef.current.children;
          if (panels[leftIndex * 2] && panels[rightIndex * 2]) {
            const leftPanel = panels[leftIndex * 2] as HTMLElement;
            const rightPanel = panels[rightIndex * 2] as HTMLElement;
            leftPanel.style.flexGrow = String(newSizes[leftIndex]);
            rightPanel.style.flexGrow = String(newSizes[rightIndex]);
          }

          // Update state for persistence
          latestSizesRef.current = [...newSizes];
          setSizes([...newSizes]);
          if (onResize) {
            onResize([...newSizes]);
          }
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        resizerIndexRef.current = -1;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Persist the final layout (once per drag, not on every mousemove).
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(latestSizesRef.current));
          } catch {
            /* storage full / disabled — non-fatal */
          }
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [direction, sizes, minSizes, maxSizes, disabled, onResize, storageKey]
  );

  // Initialize panel sizes
  useEffect(() => {
    if (!containerRef.current) return;

    const panels = containerRef.current.children;
    sizes.forEach((size, index) => {
      const panelIndex = index * 2;
      if (panels[panelIndex]) {
        const panel = panels[panelIndex] as HTMLElement;
        panel.style.flexGrow = String(size);
      }
    });
  }, []); // Only run on mount

  return (
    <ResizableContainer
      ref={containerRef}
      $gap={gap}
      style={{ flexDirection: direction === 'horizontal' ? 'row' : 'column' }}
    >
      {children.map((child, index) => (
        <React.Fragment key={index}>
          <Panel
            $flex={sizes[index]}
            style={{
              userSelect: isResizing ? 'none' : 'auto',
            }}
          >
            {child}
          </Panel>
          {index < children.length - 1 && !disabled && (
            <Resizer
              $direction={direction}
              onMouseDown={(e) => handleMouseDown(index, e)}
            />
          )}
        </React.Fragment>
      ))}
    </ResizableContainer>
  );
};

export default ResizablePanel;

